import { EnrollmentType, StudentType } from '@wonderschool/common-base-types';
import { Button, Dialog } from '@wonderschool/common-base-ui';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { EnrollmentStatusEnum } from '../../../enrollments';
import { useOrganization } from '../../../hooks/useOrganizations';
import { studentSelected } from '../../../redux/actions/studentsActions';
import { saveStudent } from '../../studentsAPI';
import { useSelectedStudent } from '../../studentsHooks';
import { StudentStatusEnum } from '../../types';

interface EnrollDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void; // called only after successful clear
  studentName: string;
  studentEnrollmentStatus: EnrollmentStatusEnum;
}

/**
 * Dialog for enrolling and re-enrolling a student
 */
export function EnrollDialog({ isOpen, onClose, onDone, studentName, studentEnrollmentStatus }: EnrollDialogProps) {
  const { t } = useTranslation();
  const organization = useOrganization();
  const selectedStudent = useSelectedStudent();
  const dispatch = useDispatch();

  const [isSaving, setIsSaving] = useState(false);
  const title =
    studentEnrollmentStatus === EnrollmentStatusEnum.PENDING
      ? t('enrollments.enrollStudentTitle')
      : t('enrollments.clearUnenrollmentTitle');
  const description =
    studentEnrollmentStatus === EnrollmentStatusEnum.PENDING
      ? t('enrollments.enrollStudentDescription', { name: studentName })
      : t('enrollments.clearUnenrollmentConfirm', { name: studentName });
  const buttonLabel =
    studentEnrollmentStatus === EnrollmentStatusEnum.PENDING
      ? t('enrollments.enrollButton')
      : t('enrollments.clearUnenrollment');

  const handleClear = useCallback(async () => {
    if (!selectedStudent || !organization?.id) return;
    try {
      setIsSaving(true);

      const enrollment: EnrollmentType = {
        ...(selectedStudent.enrollment || {}),
        status: EnrollmentStatusEnum.ENROLLED,
        unenrolled: null,
      };

      const data: Partial<StudentType> & { status: StudentStatusEnum } = {
        id: selectedStudent.id,
        status: StudentStatusEnum.ACTIVE, // enrolling also restores an archived student
        enrollmentStatus: true, // legacy boolean follows status
        enrollment: enrollment,
      };

      await saveStudent(organization.id, data);

      const updatedStudent: StudentType = {
        ...(selectedStudent satisfies StudentType),
        status: StudentStatusEnum.ACTIVE,
        enrollmentStatus: data.enrollmentStatus ?? selectedStudent.enrollmentStatus,
        enrollment: {
          ...(selectedStudent.enrollment || {}),
          ...(data.enrollment || {}),
        } satisfies EnrollmentType,
      };
      dispatch(studentSelected(updatedStudent));

      if (onDone) onDone();
      onClose();
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, onClose, onDone, organization?.id, selectedStudent]);

  return (
    <Dialog isOpen={isOpen} onClose={onClose} hasCloseButton={true} fullScreenOnMobile={true}>
      <Dialog.Panel>
        <Dialog.Title>{title}</Dialog.Title>
        <Dialog.Description>{description}</Dialog.Description>
      </Dialog.Panel>

      <Dialog.Actions>
        <div className="grid w-full grid-cols-1 items-center gap-3 sm:grid-cols-2">
          <Button onClick={onClose} label={t('common.cancel')} extraClasses="w-full order-2 sm:order-1" />
          <Button
            onClick={handleClear}
            primary
            label={buttonLabel}
            disabled={isSaving}
            loading={isSaving}
            data-testid="clear-unenrollment-confirm-btn"
            extraClasses="w-full order-1 sm:order-2"
          />
        </div>
      </Dialog.Actions>
    </Dialog>
  );
}
